import { Component } from "preact";
import layoutStyle from "./../App/layout.scss";
import style from "./index.scss";

import { ErrorText } from "../Helpers";
import FileUploader from "../../utils/FileUploader";
import Alert from "../../utils/Alert";
import { getRedirect } from "../../api";
import { shortId } from "../../utils/uuid";

const InputLabel = ({
  id,
  isRequired,
  inputName,
  labelText,
  extraText,
  descId,
}) => {
  return (
    <div>
      <label
        class={isRequired ? layoutStyle.requiredFieldLabel : null}
        for={inputName}
        id={id}
      >
        {labelText}
        {isRequired ? (
          <span aria-hidden="true" className={layoutStyle.indicator}>
            &nbsp;*
          </span>
        ) : null}
      </label>
      {extraText ? (
        <p class={layoutStyle.inputExtraText} id={descId}>
          {extraText}
        </p>
      ) : null}
    </div>
  );
};

export const InputText = ({
  labelText,
  extraText = "",
  inputName,
  onChange,
  inputValue,
  isError,
  placeholder,
  isRequired = false,
  className = "",
}) => {
  const id = shortId();
  const inputDescId = shortId();
  const descId = extraText || isError ? inputDescId : undefined;
  return (
    <div className={className}>
      {labelText ? (
        <InputLabel
          id={id}
          descId={isError ? undefined : descId}
          isRequired={isRequired}
          inputName={inputName}
          labelText={labelText}
          extraText={extraText}
        />
      ) : null}
      <input
        id={inputName}
        type="text"
        name={inputName}
        onChange={onChange}
        value={inputValue}
        class={isError ? layoutStyle.error : null}
        onKeyPress={(e) => {
          if (e.keyCode === 13) e.preventDefault();
        }}
        placeholder={placeholder}
        aria-required={isRequired}
        aria-labelledby={id}
        aria-describedby={descId}
      ></input>
      <span
        id={!isError ? undefined : descId}
        aria-atomic="true"
        aria-live="polite"
      >
        {isError ? <ErrorText /> : null}
      </span>
    </div>
  );
};

export const InputTextArea = ({
  labelText,
  inputName,
  onChange,
  inputValue,
  isError,
  placeholder,
  isRequired = false,
  className = "",
}) => {
  const textAreaLabelId = shortId();
  const textAreaErrorId = shortId();

  return (
    <div className={className}>
      {labelText ? (
        <InputLabel
          id={textAreaLabelId}
          isRequired={isRequired}
          inputName={inputName}
          labelText={labelText}
        />
      ) : null}
      <textarea
        id={inputName}
        type="text"
        name={inputName}
        onChange={onChange}
        value={inputValue}
        class={isError ? layoutStyle.error : null}
        placeholder={placeholder}
        aria-required={isRequired}
        aria-labelledby={textAreaLabelId}
        aria-describedby={textAreaErrorId}
      ></textarea>
      <span id={textAreaErrorId} aria-atomic="true" aria-live="polite">
        {isError ? <ErrorText /> : null}
      </span>
    </div>
  );
};

export const InputRadioButton = ({
  labelText,
  inputName,
  onChange,
  inputValue,
  isError,
  checked = false,
  isRequired = false,
  className = "",
}) => {
  const radioLabelId = shortId();
  const radioErrorId = shortId();
  return (
    <div className={className}>
      <input
        type="radio"
        id={inputName}
        name={inputName}
        onChange={onChange}
        value={inputValue}
        checked={checked}
        aria-required={isRequired}
        aria-labelledby={radioLabelId}
        aria-describedby={radioErrorId}
      ></input>
      <label
        class={isRequired ? layoutStyle.requiredFieldLabel : null}
        for={inputName}
        id={radioLabelId}
      >
        {labelText}
      </label>
      <span id={radioErrorId} aria-atomic="true" aria-live="polite">
        {isError ? <ErrorText /> : null}
      </span>
    </div>
  );
};

export class InputFile extends Component {
  addFiles = (newFiles) => {
    const { handleFileUpload, files } = this.props;
    const { filesUploaded = [] } = newFiles;
    if (filesUploaded.length > 0) {
      const clonedFiles = filesUploaded.concat(files || []);
      handleFileUpload(clonedFiles);
      Alert.success("File uploaded successfully");
    } else {
      // console.log("new files - ", newFiles);
      Alert.error("Error uploading file! Please try again.");
    }
  };
  removeFile = (key) => {
    const { files, handleFileUpload } = this.props;
    const clonedFiles = [...(files || [])];
    clonedFiles.splice(key, 1);
    handleFileUpload(clonedFiles);
  };
  render({
    payload,
    label,
    files = [],
    extraText = "",
    hideExtraText = false,
  }) {
    const fileLabelId = shortId();
    const fileDescId = shortId();

    const labelText = label || "Add a screenshot or video (recommended)";
    return (
      <div style={{ textAlign: "left" }}>
        <label id={fileLabelId}>{labelText}</label>
        {!hideExtraText && (
          <p id={fileDescId} class={layoutStyle.inputExtraText}>
            {extraText}
            {payload.isClientAdmin ? (
              <a
                href="#"
                style={{ marginLeft: "0.3rem" }}
                onClick={() =>
                  getRedirect({
                    queryStr:
                      "https://support.peoplegrove.com/hc/en-us/articles/115001964112-Access-PeopleGrove-Support#tips_and_best_practices",
                    payload,
                  })
                }
              >
                Not sure how?
              </a>
            ) : null}
          </p>
        )}
        <FileUploader
          path="/support/ticket/"
          render={({ onPick }) => (
            <button
              aria-labelledby={fileLabelId}
              aria-describedby={!hideExtraText ? fileDescId : undefined}
              onClick={onPick}
              type="button"
              class="upload-btn"
            >
              {`Add File(s)`}
            </button>
          )}
          onFileUpload={this.addFiles.bind(this)}
          access="private"
          trigger="support-trigger"
          hubId={payload.hubId}
          payload={payload}
          resourceType="support"
        />
        {/* Display list of uploaded additional documents along with their respective file sizes*/}
        {files && files.length > 0 ? (
          <ul style={{ marginBottom: "1.5rem" }}>
            {files.map((doc, key) => (
              <li key={key} class={layoutStyle.inputExtraText}>
                <span>
                  {doc.file.name} ({Math.round(doc.file.size / 1024)}KB)
                  <em
                    onClick={() => this.removeFile(key)}
                    tabIndex="0"
                    role="button"
                    aria-label={"remove file " + doc.file.name}
                    class={
                      "pg-support-widget-cross" +
                      " " +
                      style.fileInputRemoveIcon
                    }
                  ></em>
                </span>
              </li>
            ))}
          </ul>
        ) : null}
      </div>
    );
  }
}



// WEBPACK FOOTER //
// ./components/FormFields/index.js